import { Chip, Popover, Stack, styled } from '@mui/material';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { map } from 'lodash';
import pluralize from 'pluralize';
import React, { useEffect, useRef, useState } from 'react';

export interface MultiplexMarkerInfo {
  key: string;
  markerId: string;
  label: string;
  color?: string;
  secondaryLabel?: string;
  opensMarkerNamingForm?: boolean;
}

const CompactChip = styled(Chip)(({ theme }) => ({
  maxWidth: '100%',
  height: 22,
  borderRadius: 999,
  '& .MuiChip-label': {
    display: 'block',
    overflow: 'hidden',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
}));

const PopoverEyebrowTypography = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  lineHeight: 1.2,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}));

export interface MultiplexMarkersChipProps {
  markerInfos: MultiplexMarkerInfo[];
  dismissToken?: number;
  onOpenChange?: (isOpen: boolean) => void;
  appearance?: 'card' | 'table';
  popoverTitle?: string;
  chipLabel?: string;
  guidance?: string;
  emphasizeIncomplete?: boolean;
  onChipAction?: () => boolean;
  onMarkerAction?: (markerInfo: MultiplexMarkerInfo) => boolean;
}

export const MultiplexMarkersChip = ({
  markerInfos,
  dismissToken,
  onOpenChange,
  appearance = 'card',
  popoverTitle = 'Markers',
  chipLabel,
  guidance,
  emphasizeIncomplete = false,
  onChipAction,
  onMarkerAction,
}: MultiplexMarkersChipProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const previousDismissToken = useRef(dismissToken);
  const isTableAppearance = appearance === 'table';
  const isPopoverOpen = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl((currentAnchorEl) => {
      if (currentAnchorEl) {
        onOpenChange?.(false);
      }
      return null;
    });
  };

  useEffect(() => {
    if (dismissToken === previousDismissToken.current) {
      return;
    }

    previousDismissToken.current = dismissToken;
    handleClose();
  }, [dismissToken]);

  useEffect(() => {
    return () => {
      onOpenChange?.(false);
    };
  }, [onOpenChange]);

  if (!markerInfos.length) {
    return null;
  }

  const tableChipSx = isTableAppearance
    ? {
        alignSelf: 'center',
        '& .MuiChip-label': {
          px: 0.875,
          fontSize: '0.72rem',
          fontWeight: 600,
        },
      }
    : undefined;

  return (
    <>
      <CompactChip
        label={chipLabel || `${markerInfos.length} ${pluralize('marker', markerInfos.length)}`}
        size="small"
        color={emphasizeIncomplete ? 'warning' : isTableAppearance ? 'default' : 'secondary'}
        variant={emphasizeIncomplete || !isTableAppearance ? 'filled' : 'outlined'}
        clickable
        sx={
          isTableAppearance && !emphasizeIncomplete
            ? {
                ...tableChipSx,
                borderColor: theme.palette.divider,
                backgroundColor: alpha(theme.palette.action.hover, theme.palette.mode === 'light' ? 0.18 : 0.26),
                color: theme.palette.text.secondary,
                '&.MuiChip-clickable:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, theme.palette.mode === 'light' ? 0.28 : 0.36),
                },
              }
            : tableChipSx
        }
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (onChipAction?.()) {
            return;
          }
          onOpenChange?.(true);
          setAnchorEl(event.currentTarget);
        }}
      />

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          'data-testid': 'multiplex-markers-popover',
          sx: {
            mt: 0.75,
            borderRadius: 2,
            p: 1.5,
            minWidth: 220,
            maxWidth: 280,
          },
        }}
      >
        <Stack spacing={1} onClick={(event) => event.stopPropagation()}>
          <PopoverEyebrowTypography>{popoverTitle}</PopoverEyebrowTypography>
          {guidance ? (
            <Typography variant="caption" color="text.secondary">
              {guidance}
            </Typography>
          ) : null}
          <Stack component="ul" spacing={0.9} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {map(markerInfos, (markerInfo) => (
              <Box
                key={markerInfo.key}
                component="li"
                onClick={
                  markerInfo.opensMarkerNamingForm
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (onMarkerAction?.(markerInfo)) {
                          handleClose();
                        }
                      }
                    : undefined
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  cursor: markerInfo.opensMarkerNamingForm ? 'pointer' : 'default',
                }}
              >
                {!isTableAppearance && markerInfo.color && (
                  <Box
                    data-testid={`multiplex-marker-swatch-${markerInfo.markerId}`}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: markerInfo.color,
                      boxShadow: `0 0 0 1px ${theme.palette.divider}`,
                    }}
                  />
                )}
                <Stack spacing={0} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    data-testid="multiplex-marker-label"
                    sx={{ minWidth: 0, fontWeight: 500 }}
                    noWrap
                    title={markerInfo.label}
                  >
                    {markerInfo.label}
                  </Typography>
                  {markerInfo.secondaryLabel ? (
                    <Typography variant="caption" color="text.secondary" noWrap title={markerInfo.secondaryLabel}>
                      {markerInfo.secondaryLabel}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
